import { Label, PolarRadiusAxis, RadialBar, RadialBarChart } from "recharts";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartConfig, ChartContainer } from "@/components/ui/chart";
import { Progress } from "@/components/ui/progress";
import { T_WasteManagementData } from "@/interfaces/customer-portal/waste-management.interface";
import { cn } from "@/lib/utils";

const chartConfig = {
  recycle: {
    label: "Recycle",
    color: "#7367F0",
  },
  nonRecycle: {
    label: "Non-Recycle",
    color: "#e5e7eb",
  },
} satisfies ChartConfig;

type T_RecyclePercentageProps = {
  wasteManagementData: T_WasteManagementData;
  className?: string;
};

export const RecyclePercentage = ({
  wasteManagementData,
  className,
}: T_RecyclePercentageProps): React.JSX.Element => {
  const recyclingPercentage = wasteManagementData?.recyclingPercentage;
  const getPercentage = (value: number): number => {
    if (!value) {
      return 0;
    }

    return Number(Math.abs(value ?? 0).toFixed(2));
  };

  const finalData = [
    {
      name: "Recycle",
      recycle: getPercentage(recyclingPercentage?.recyclingPercentage),
      nonRecycle: 100 - getPercentage(recyclingPercentage?.recyclingPercentage),
    },
  ];

  return (
    <Card className={cn("flex flex-col ", className)}>
      <CardHeader className='items-center pb-0'>
        <CardTitle className='w-full text-xl font-normal '>
          Recycling Percentage
        </CardTitle>
      </CardHeader>
      <CardContent className='flex items-center pt-0 h-full'>
        <ChartContainer
          config={chartConfig}
          className='flex-1 w-full mx-auto aspect-square '
        >
          <RadialBarChart
            data={finalData}
            startAngle={0}
            endAngle={360}
            innerRadius={110}
            outerRadius={160}
          >
            <PolarRadiusAxis tick={false} tickLine={false} axisLine={false}>
              <Label
                content={({ viewBox }): React.JSX.Element => {
                  if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                    return (
                      <text
                        x={viewBox.cx}
                        y={viewBox.cy}
                        textAnchor='middle'
                        dominantBaseline='middle'
                      >
                        <tspan
                          x={viewBox.cx}
                          y={viewBox.cy}
                          className='text-4xl font-medium fill-foreground'
                        >
                          {finalData[0].recycle
                            ? finalData[0].recycle.toLocaleString()
                            : 0}{" "}
                          %
                        </tspan>
                      </text>
                    );
                  }

                  return <></>;
                }}
              />
            </PolarRadiusAxis>
            <RadialBar
              dataKey='recycle'
              cornerRadius={5}
              stackId={"a"}
              fill={chartConfig.recycle.color}
            />
            <RadialBar
              dataKey='nonRecycle'
              stackId='a'
              cornerRadius={0}
              fill={chartConfig.nonRecycle.color}
            />
          </RadialBarChart>
        </ChartContainer>
        <div className='flex flex-col gap-2 m-auto'>
          <div className='space-y-1'>
            <div className='flex items-center gap-1'>
              {/* <div className={`border-r border-4 w-2 border-[#7367F0]`} /> */}
              <p className='text-sm text-black/70'>Recycle</p>
            </div>
            <Progress
              segments={[
                {
                  value: getPercentage(
                    recyclingPercentage?.recyclingPercentage || 0,
                  ),
                  color: "#7367F0",
                },
              ]}
              className={`bg-gray-200 w-full h-2`}
              color='yellow'
            />
            <p className='text-sm text-aldgray'>
              {getPercentage(recyclingPercentage?.recyclingPercentage || 0)}%
            </p>
          </div>
          <div className='space-y-1'>
            <div className='flex items-center gap-1'>
              {/* <div className={`border-r border-4 w-2 border-[#E4E8F2]`} /> */}
              <p className='text-sm text-black/70'>Non-Recycle</p>
            </div>
            <Progress
              segments={[
                {
                  value: getPercentage(
                    recyclingPercentage?.nonRecyclingPercentage || 0,
                  ),
                  color: "#e5e7eb",
                },
              ]}
              className={`bg-gray-200  w-full h-2`}
            />
            <p className='text-sm text-aldgray'>
              {getPercentage(recyclingPercentage?.nonRecyclingPercentage || 0)}%
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
